<template>
  <div>
    <slot
      :getDataField="getDataField"
      :getFilteredDataField="getFilteredDataField"
    >
      <div class="row position-relative">
        <div class="col-12 col-lg-7 mt-0 mt-lg-2">
          <item-image-carousel
            :item-id="itemId"
            :image-urls-data="
              currentVariation.images.variation.length > 0
                ? currentVariation.images.variation
                : currentVariation.images.all.slice(0, 5)
            "
            :name="itemName"
            :multi-language="{
              ProductBackgroundVariations:
                multiLanguage.ProductBackgroundVariations,
            }"
            ref="itemImageCarousel"
          ></item-image-carousel>
        </div>

        <div class="col-12 col-lg-5 pl-3 pl-lg-0">
          <slot name="item-reviews"></slot>

          <h1 class="h1 title mt-2 mb-3">{{ itemName }}</h1>

          <div
            class="price-container d-flex justify-content-between w-100 position-relative my-3 align-items-center"
          >
            <div class="price price-gross-net">
              <div class="actual-price-data d-flex align-items-center">
                <span class="actual-price">
                  {{ multiLanguage.ProductShopPrice }}:
                </span>
                <del class="discounted-price">
                  {{
                    ((isNet
                      ? currentVariation.prices.rrp.data.unitPriceNet
                      : currentVariation.prices.rrp.data.unitPrice) +
                      variationPropertySurcharge)
                      | currency
                  }}
                </del>
              </div>
              <div
                v-if="
                  currentVariation.prices.default.categoryDiscount.amount > 0
                "
                class="discounted-price-data d-flex align-items-center"
              >
                <del class="discounted-price text-primary">
                  {{
                    ((isNet
                      ? variationGraduatedPrice.data.unitPriceNet
                      : variationGraduatedPrice.data.unitPrice) +
                      variationPropertySurcharge +
                      currentVariation.prices.default.categoryDiscount.amount)
                      | currency
                  }}
                </del>
                <div class="offer">
                  -{{
                    currentVariation.prices.default.categoryDiscount.percent
                  }}% {{ multiLanguage.ProductExtra }}
                </div>
              </div>
              <div class="total-price-data d-flex align-items-center">
                <span class="total-price">
                  {{
                    ((isNet
                      ? variationGraduatedPrice.data.unitPriceNet
                      : variationGraduatedPrice.data.unitPrice) +
                      variationPropertySurcharge)
                      | currency
                  }}
                </span>
                <span class="item-vat">
                  {{
                    isNet
                      ? multiLanguage.ProductIncludingNet
                      : ["£", "CHF", "NOK", "US$"].includes(currency)
                      ? multiLanguage.ProductExcludingVat
                      : multiLanguage.ProductIncludingVat
                  }}
                </span>
              </div>
            </div>

            <div class="icons d-flex justify-content-center align-items-center">
              <add-to-wish-list
                :variation-id="currentVariation.variation.id"
              ></add-to-wish-list>
            </div>
          </div>

          <other-forms
            :item-id="itemId"
            :multi-language="{
              ProductOtherForms: multiLanguage.ProductOtherForms,
            }"
          ></other-forms>

          <variation-select
            :item-id="itemId"
            :multi-language="{
              VariationSelectSize: multiLanguage.VariationSelectSize,
              ShopBySizes: multiLanguage.ShopBySizes,
              ProductInstock: multiLanguage.ProductInstock,
              ProductOutOfStock: multiLanguage.ProductOutOfStock,
            }"
          ></variation-select>

          <item-bundle
            v-if="currentVariation.variation.bundleType === 'bundle'"
            :bundle-type="currentVariation.variation.bundleType"
            :bundle-components="currentVariation.bundleComponents"
          ></item-bundle>

          <div
            v-if="
              language == 'de' &&
              currentVariation.filter.isSalable &&
              variationGroupedProperties.length
            "
          >
            <order-property-list></order-property-list>
          </div>

          <div id="single-item-promo-bar" class="my-3 text-center">
            <slot name="item-code"></slot>
          </div>

          <div class="add-to-cart" id="addToCartButton">
            <add-to-basket
              :show-buy-button="true"
              :variation-id="currentVariation.variation.id"
              :is-salable="
                !!currentVariation.filter && currentVariation.filter.isSalable
              "
              :has-children="
                !!currentVariation.filter &&
                currentVariation.filter.hasActiveChildren
              "
              :interval-quantity="
                currentVariation.variation.intervalOrderQuantity || 1
              "
              :minimum-quantity="
                currentVariation.variation.minimumOrderQuantity
              "
              :maximum-quantity="
                !!currentVariation.variation.maximumOrderQuantity &&
                currentVariation.variation.maximumOrderQuantity > 0
                  ? currentVariation.variation.maximumOrderQuantity
                  : null
              "
              :order-properties="
                currentVariation.properties.filter((prop) => {
                  return prop.property.isOderProperty;
                })
              "
              :has-order-properties="currentVariation.hasOrderProperties"
              :has-required-order-property="
                currentVariation.hasRequiredOrderProperty
              "
              :use-large-scale="false"
              :show-quantity="true"
              :item-url="currentVariation | itemURL"
              :is-variation-selected="
                isVariationSelected && currentVariation.filter.isSalable
              "
              :has-price="currentVariation | hasItemDefaultPrice"
              :multi-language="{
                ProductBuyNow: multiLanguage.ProductBuyNow,
              }"
            ></add-to-basket>
          </div>

          <availability-request
            :multi-language="{
              ContactEmail: multiLanguage.ContactEmail,
              ContactEmailPlaceholder: multiLanguage.ContactEmailPlaceholder,
              ContactNumber: multiLanguage.ContactNumber,
              ContactNumberPlaceholder: multiLanguage.ContactNumberPlaceholder,
              AvailabilityTitle: multiLanguage.AvailabilityTitle,
              AvailabilityText: multiLanguage.AvailabilityText,
              AvailabilityButton: multiLanguage.AvailabilityButton,
              EmailFieldRequired: multiLanguage.EmailFieldRequired,
              PhoneNumberFieldRequired: multiLanguage.PhoneNumberFieldRequired,
            }"
          ></availability-request>

          <graduated-prices></graduated-prices>

          <div class="single-item-info-card my-3">
            <ul class="list-group list-group-flush">
              <li class="list-group-item d-flex align-items-center">
                <slot name="item-availability-delivery"></slot>
                <span v-if="availability.names && availability.names.name">
                  : {{ availability.names.name }}
                </span>
              </li>
              <li
                class="list-group-item d-flex align-items-center justify-content-between flex-wrap"
              >
                <slot name="item-availability-shipping"></slot>
              </li>
              <slot
                v-if="guaranteeProduct"
                name="item-availability-guarantee"
              ></slot>
              <slot name="item-availability-return"></slot>
              <slot name="item-availability-direct"></slot>
              <slot
                v-if="
                  certificateOfAuthenticity &&
                  certificateOfAuthenticity.length > 0
                "
                name="item-certificate-authenticity"
              ></slot>
            </ul>
          </div>
        </div>
      </div>
      <div class="my-4 item-details-container" id="item-details">
        <div class="accordion-item">
          <button
            class="accordion-title d-block w-100 position-relative text-left cursor-pointer py-3"
            id="accordionDetails"
            data-toggle="collapse"
            data-target="#collapseDetails"
            aria-expanded="true"
            aria-controls="collapseDetails"
          >
            <p class="h4">{{ multiLanguage.ProductDetailsTitle }}</p>
          </button>
          <div
            id="collapseDetails"
            class="collapse show"
            aria-labelledby="accordionDetails"
          >
            <div class="row item-details py-3">
              <div class="col-12 col-lg-6">
                <div class="my-2">
                  <p class="h5 mb-2">
                    {{ multiLanguage.ProductId }}
                    {{ currentVariation.variation.id }}
                  </p>
                  <slot name="item-note"></slot>
                </div>
                <div class="my-4">
                  <p class="h5 mb-2">{{ multiLanguage.ProductDetails }}</p>
                  <p>{{ currentVariation.texts.shortDescription }}</p>
                </div>
                <div class="my-4">
                  <p class="h5 mb-2">
                    {{ multiLanguage.ProductMoreInformation }}
                  </p>
                  <ul>
                    <li v-for="item in filteredPropertiesList" :key="item.id">
                      {{ item.values.value }}
                    </li>
                  </ul>
                </div>
              </div>
              <div class="col-12 col-lg-6 item-detail-row flex-wrap px-3">
                <div class="mb-4 col-12 col-md-6 col-lg-12 px-0">
                  <p class="h5 mb-2">
                    {{ multiLanguage.ProductMaterialQuality }}
                  </p>
                  <div
                    class="d-flex materials-list"
                    v-for="item in filteredMaterialList"
                    :key="item.id"
                  >
                    <p class="w-50">{{ item.names ? item.names.name : "" }}</p>
                    <div
                      class="w-50 d-flex"
                      v-if="
                        item.id === 345 || item.id === 348 || item.id === 678
                      "
                    >
                      <span>{{ item.values.value }}</span>
                      <div
                        class="item-feature position-relative cursor-pointer ml-2"
                        v-if="item.values.description"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 50 50"
                          width="16"
                          height="16"
                        >
                          <path
                            d="M25,2C12.297,2,2,12.297,2,25s10.297,23,23,23s23-10.297,23-23S37.703,2,25,2z M25,11c1.657,0,3,1.343,3,3s-1.343,3-3,3 s-3-1.343-3-3S23.343,11,25,11z M29,38h-2h-4h-2v-2h2V23h-2v-2h2h4v2v13h2V38z"
                          />
                        </svg>
                        <div class="feature d-none">
                          {{ item.values.description }}
                        </div>
                      </div>
                      <div
                        class="item-feature position-relative cursor-pointer ml-2"
                        v-if="
                          item.names &&
                          item.names.description &&
                          isWool(item.id, item.values.value)
                        "
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 50 50"
                          width="16"
                          height="16"
                        >
                          <path
                            d="M25,2C12.297,2,2,12.297,2,25s10.297,23,23,23s23-10.297,23-23S37.703,2,25,2z M25,11c1.657,0,3,1.343,3,3s-1.343,3-3,3 s-3-1.343-3-3S23.343,11,25,11z M29,38h-2h-4h-2v-2h2V23h-2v-2h2h4v2v13h2V38z"
                          />
                        </svg>
                        <div class="feature d-none">
                          {{ item.names.description }}
                        </div>
                      </div>
                    </div>
                    <p class="w-50 d-flex" v-else>{{ item.values.value }}</p>
                  </div>
                </div>
                <div class="mb-4 col-12 col-md-6 col-lg-12 px-0">
                  <p class="h5 mb-2">
                    {{ multiLanguage.ProductMeasureWeight }}
                  </p>
                  <div
                    class="d-flex my-1 materials-list"
                    v-for="item in filteredDimensionList"
                    :key="item.id"
                  >
                    <p class="w-50">{{ item.names ? item.names.name : "" }}</p>
                    <div class="w-50 d-flex" v-if="item.id == 659">
                      <span>{{ item.values.value }}</span>
                      <div
                        class="item-feature position-relative cursor-pointer ml-2"
                        v-if="item.values.description"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 50 50"
                          width="16"
                          height="16"
                        >
                          <path
                            d="M25,2C12.297,2,2,12.297,2,25s10.297,23,23,23s23-10.297,23-23S37.703,2,25,2z M25,11c1.657,0,3,1.343,3,3s-1.343,3-3,3 s-3-1.343-3-3S23.343,11,25,11z M29,38h-2h-4h-2v-2h2V23h-2v-2h2h4v2v13h2V38z"
                          />
                        </svg>
                        <div class="feature d-none">
                          {{ item.values.description }}
                        </div>
                      </div>
                    </div>
                    <div
                      class="w-50 d-flex align-items-center flex-wrap"
                      v-else-if="item.id === 657"
                    >
                      <label for="dimension" class="mr-2">
                        {{
                          initialMeasure === "cm"
                            ? item.values.value
                            : currentMeasure
                        }}
                      </label>
                      <select
                        id="dimension"
                        name="dimension"
                        aria-labelledby="dimension"
                        v-model="initialMeasure"
                        @change="handleSizeChange"
                      >
                        <option value="cm">cm</option>
                        <option value="ft">ft</option>
                      </select>
                    </div>
                    <div class="w-50 d-flex" v-else>
                      {{ item.values.value }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="accordion-item">
          <button
            class="accordion-title d-block w-100 position-relative text-left cursor-pointer py-3 collapsed"
            id="accordionAdvantages"
            data-toggle="collapse"
            data-target="#collapseAdvantages"
            aria-expanded="false"
            aria-controls="collapseAdvantages"
          >
            <p class="h4">{{ multiLanguage.ProductAdvantagesTitle }}</p>
          </button>
          <div
            id="collapseAdvantages"
            class="collapse"
            aria-labelledby="accordionAdvantages"
          >
            <div class="py-3">
              <slot name="accordion-advantages"></slot>
            </div>
          </div>
        </div>
        <div class="accordion-item">
          <button
            class="accordion-title d-block w-100 position-relative text-left cursor-pointer py-3 collapsed"
            id="accordionGurantee"
            data-toggle="collapse"
            data-target="#collapseGurantee"
            aria-expanded="false"
            aria-controls="collapseGurantee"
          >
            <p class="h4">{{ multiLanguage.ProductGuaranteeTitle }}</p>
          </button>
          <div
            id="collapseGurantee"
            class="collapse"
            aria-labelledby="accordionGurantee"
          >
            <div class="py-3">
              <slot name="accordion-guarantees"></slot>
            </div>
          </div>
        </div>
        <div class="accordion-item">
          <button
            class="accordion-title d-block w-100 position-relative text-left cursor-pointer py-3 collapsed"
            id="accordionCare"
            data-toggle="collapse"
            data-target="#collapseCare"
            aria-expanded="false"
            aria-controls="collapseCare"
          >
            <p class="h4">{{ multiLanguage.ProductCareTitle }}</p>
          </button>
          <div
            id="collapseCare"
            class="collapse"
            aria-labelledby="accordionCare"
          >
            <div class="py-3">
              <slot name="accordion-care"></slot>
            </div>
          </div>
        </div>
        <div
          class="accordion-item contact-item py-3 d-flex align-items-center justify-content-between flex-wrap"
          style="gap: 1rem"
        >
          <slot name="accordion-contact"></slot>
        </div>
      </div>
      <lazy-hydrate when-idle>
        <add-to-basket-sticky>
          <template>
            <div
              class="d-flex justify-content-between align-sm-items-center container-max mx-auto flex-column flex-sm-row"
            >
              <div class="mr-3 d-none d-md-block">
                <img
                  class="img-fluid"
                  :src="
                    currentVariation.images.variation.length > 0
                      ? currentVariation.images.variation[0].urlPreview
                      : currentVariation.images.all[0].urlPreview
                  "
                  :alt="itemName"
                  :title="itemName"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div
                class="title-price d-flex flex-row flex-md-column align-items-center align-items-md-start justify-content-start justify-content-md-center"
              >
                <p class="product-title overflow-hidden">{{ itemName }}</p>
                <div class="price d-flex align-items-center ml-3 ml-md-0">
                  <del class="total-price text-muted mr-2">{{
                    ((isNet
                      ? currentVariation.prices.rrp.data.unitPriceNet
                      : currentVariation.prices.rrp.data.unitPrice) +
                      variationPropertySurcharge)
                      | currency
                  }}</del>
                  <span class="discounted-price h5 font-weight-medium">
                    {{
                      ((isNet
                        ? variationGraduatedPrice.data.unitPriceNet
                        : variationGraduatedPrice.data.unitPrice) +
                        variationPropertySurcharge)
                        | currency
                    }}
                  </span>
                </div>
              </div>
              <div class="item-advantage d-flex align-items-center">
                <slot name="item-availability-delivery"></slot>
                <span v-if="availability.names && availability.names.name">
                  : {{ availability.names.name }}
                </span>
              </div>
              <div
                class="basket-button d-block d-md-flex align-items-center justify-content-center ml-0 ml-sm-3 mt-2 mt-sm-0"
              >
                <add-to-basket
                  :show-buy-button="false"
                  :variation-id="currentVariation.variation.id"
                  :is-salable="
                    !!currentVariation.filter &&
                    currentVariation.filter.isSalable
                  "
                  :has-children="
                    !!currentVariation.filter &&
                    currentVariation.filter.hasActiveChildren
                  "
                  :interval-quantity="
                    currentVariation.variation.intervalOrderQuantity || 1
                  "
                  :minimum-quantity="
                    currentVariation.variation.minimumOrderQuantity
                  "
                  :maximum-quantity="
                    !!currentVariation.variation.maximumOrderQuantity &&
                    currentVariation.variation.maximumOrderQuantity > 0
                      ? currentVariation.variation.maximumOrderQuantity
                      : null
                  "
                  :order-properties="
                    currentVariation.properties.filter((prop) => {
                      return prop.property.isOderProperty;
                    })
                  "
                  :has-order-properties="currentVariation.hasOrderProperties"
                  :has-required-order-property="
                    currentVariation.hasRequiredOrderProperty
                  "
                  :use-large-scale="false"
                  :show-quantity="true"
                  :item-url="currentVariation | itemURL"
                  :is-variation-selected="
                    isVariationSelected && currentVariation.filter.isSalable
                  "
                  :has-price="currentVariation | hasItemDefaultPrice"
                  :multi-language="{
                    ProductBuyNow: multiLanguage.ProductBuyNow,
                  }"
                />
              </div>
            </div>
          </template>
        </add-to-basket-sticky>
      </lazy-hydrate>
    </slot>
  </div>
</template>

<script>
import { get } from "../../helper/get";
import { isNullOrUndefined } from "../../helper/utils";
import { mapState } from "vuex";

export default {
  name: "single-item",
  components: {
    "item-image-carousel": () => import("./ItemImageCarousel.vue"),
    "availability-request": () => import("./AvailabilityRequest.vue"),
    "series-badge": () => import("./SeriesBadge.vue"),
    "other-forms": () => import("./OtherForms.vue"),
    "variation-select": () => import("./VariationSelect.vue"),
    "order-property-list": () => import("./OrderPropertyList.vue"),
    "graduated-prices": () => import("./GraduatedPrices.vue"),
    "add-to-basket-sticky": () => import("./AddToBasketSticky.vue"),
  },
  props: {
    pleaseSelectOptionVariationId: {
      type: Number,
      default: 0,
    },
    initPleaseSelectOption: {
      type: Boolean,
      default: false,
    },
    itemId: {
      type: Number,
      required: true,
    },
    afterKey: Object,
    language: {
      type: String,
      default: "de",
    },
    currency: {
      type: String,
      default: "EUR",
    },
    premiumServiceLink: String,
    homeServiceLink: String,
    multiLanguage: Object,
  },

  jsonDataFields: ["itemData", "attributesData", "variations"],

  provide() {
    return {
      itemId: this.$props.itemId,
    };
  },

  data() {
    return {
      currentMeasure: "",
      initialMeasure: "cm",
    };
  },

  computed: {
    ...mapState({
      basket: (state) => state.basket.data,
    }),

    productPrice() {
      return (
        (this.isNet
          ? this.variationGraduatedPrice.data.unitPriceNet
          : this.variationGraduatedPrice.data.unitPrice) +
        this.variationPropertySurcharge
      );
    },

    variationGroupedProperties() {
      return this.$store.getters[`${this.itemId}/variationGroupedProperties`];
    },

    variationMissingProperties() {
      return this.$store.getters[`${this.itemId}/variationMissingProperties`];
    },

    itemName() {
      return this.currentVariation.texts.name3.split(",")[0];
    },

    isNet() {
      return this.currentVariation.prices.default.isNet;
    },

    currentVariation() {
      return this.$store.getters[`${this.itemId}/currentItemVariation`];
    },

    variationData() {
      return this.currentVariation.variationProperties;
    },

    variationProperties() {
      if (!this.variationData) {
        return [];
      }
      return this.variationData.filter((item) => item.id == 30);
    },

    filteredMaterialList() {
      if (this.variationProperties.length <= 0) {
        return [];
      }
      return (
        this.variationProperties[0]?.properties.filter((item) =>
          [345, 346, 348, 349, 350, 351, 633, 678].includes(item.id)
        ) || []
      );
    },

    filteredDimensionList() {
      if (this.variationProperties.length <= 0) {
        return [];
      }
      return (
        this.variationProperties[0]?.properties.filter((item) =>
          [657, 658, 659].includes(item.id)
        ) || []
      );
    },

    filteredPropertiesList() {
      if (this.variationProperties.length <= 0) {
        return [];
      }
      return (
        this.variationProperties[0]?.properties.filter((item) =>
          [660, 661, 662, 663, 664].includes(item.id)
        ) || []
      );
    },

    variationPropertySurcharge() {
      return this.$store.getters[`${this.itemId}/variationPropertySurcharge`];
    },

    variationGraduatedPrice() {
      return this.$store.getters[`${this.itemId}/variationGraduatedPrice`];
    },

    isVariationSelected() {
      return get(
        this.$store.state,
        `items[${this.itemId}].variationSelect.isVariationSelected`
      );
    },

    units() {
      return get(
        this.$store.state,
        `items[${this.itemId}].variationSelect.units`
      );
    },

    isItemSet() {
      return this.$store.state.items.isItemSet;
    },

    guaranteeProduct() {
      return this.currentVariation?.variationProperties?.[0]?.properties?.some(
        (item) =>
          item.id == 345 &&
          item.values.value === this.multiLanguage.ProductHandKnotted
      );
    },

    certificateOfAuthenticity() {
      const properties =
        this.currentVariation?.variationProperties?.[1]?.properties || [];
      const itemTags = properties.find((item) => item.id === 830)?.values || [];
      return itemTags.filter((tag) => tag.selectionId === 2810);
    },

    availability() {
      return this.currentVariation?.variation?.availability || {};
    },
  },

  created() {
    this.$store.dispatch("initVariation", this.itemData);
    this.$store.commit(
      `${this.itemId}/setPleaseSelectVariationId`,
      this.pleaseSelectOptionVariationId
    );
  },

  mounted() {
    this.$nextTick(() => {
      this.$store.dispatch(
        `${this.itemId}/variationSelect/setVariationSelect`,
        {
          itemId: this.itemId,
          attributes: this.attributesData,
          variations: this.variations,
          initialVariationId: this.currentVariation.variation.id,
          isPleaseSelectOption: this.initPleaseSelectOption,
          afterKey: this.afterKey,
        }
      );

      if (this.isItemSet) {
        this.$store.dispatch("initSetComponents", this.itemData);
      }
    });

    document.addEventListener("onVariationChanged", () =>
      this.hydrateChildren(this.$children)
    );
  },

  methods: {
    isWool(id, name = "") {
      const arr = ["wolle", "wool", "vierge", "wol"];
      if (id === 678) {
        return arr.some((keyword) => name.toLowerCase().includes(keyword));
      }
      return true;
    },
    convertToFeet: (n) => {
      return (n * 0.3937) / 12;
    },
    handleSizeChange() {
      this.filteredDimensionList.forEach((item) => {
        if (item.id === 657 && item.values.value) {
          if (this.currentMeasure.includes("ft")) {
            this.initialMeasure = "cm";
            this.currentMeasure = "";
          } else {
            const values = item.values.value.split(" x ");
            let convertedValues = values.map((val) =>
              new Intl.NumberFormat("de-DE", {
                minimumFractionDigits: 2,
              }).format(
                this.convertToFeet(
                  parseFloat(val.replace(".", "").replace(",", "."))
                ).toFixed(2)
              )
            );
            this.currentMeasure = convertedValues.join(" x ") + " ft";
            this.initialMeasure = "ft";
          }
        }
      });
    },

    getDataField(field) {
      return get(this.currentVariation, field);
    },

    getFilteredDataField(field, filter) {
      if (!isNullOrUndefined(this.$options.filters[filter])) {
        return this.$options.filters[filter](this.getDataField(field));
      }

      return this.getDataField(field);
    },

    hydrateChildren(nodes) {
      nodes.forEach((component) => {
        if (component.$options.name === "lazy-hydrate") {
          component.hydrate();
        } else if (component.$children.length) {
          this.hydrateChildren(component.$children);
        }
      });
    },
  },
};
</script>
